import { useTranslation } from 'react-i18next';
import { FormDialog } from '@/shared/ui/FormDialog';

export interface ExportWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rows missing a value in a cell that applies to them. */
  incomplete: number;
  total: number;
  /** The schema carries a rule the sheet's formula cannot express. */
  rulesEscapeFormula: boolean;
  /**
   * The name column will carry a fixed string instead of a live calculation,
   * because a field the name is built from cannot be put in the sheet at all.
   */
  nameFormulaWithheld: boolean;
  /** The grid holds edits the server has not seen. */
  unsaved: boolean;
  onConfirm: () => void;
}

/**
 * What the workbook will and will not contain, said before it downloads.
 *
 * Shown ONLY when it has something to say — an export with nothing to warn about
 * downloads straight away, because a dialog that always appears is one people
 * learn to dismiss without reading.
 *
 * Nothing is withheld from the file. An earlier design excluded incomplete rows,
 * which would have made three comments in `taxonomy-dataset.service.ts` false and
 * put the count and the file's contents in two places that could disagree; the
 * export stays a faithful dump of what is stored, and this says what that means.
 */
export function ExportWarningDialog({
  open,
  onOpenChange,
  incomplete,
  total,
  rulesEscapeFormula,
  nameFormulaWithheld,
  unsaved,
  onConfirm,
}: ExportWarningDialogProps) {
  const { t } = useTranslation('knowledge');

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('taxonomy.exportWarning.title')}
      description={t('taxonomy.exportWarning.description')}
    >
      <FormDialog.Body>
        <ul className="flex list-disc flex-col gap-sm pl-4 text-sm text-muted-foreground">
          {incomplete > 0 && (
            <li>{t('taxonomy.exportWarning.incomplete', { count: incomplete, total })}</li>
          )}
          {rulesEscapeFormula && <li>{t('taxonomy.exportWarning.rules')}</li>}
          {nameFormulaWithheld && <li>{t('taxonomy.exportWarning.nameNotLive')}</li>}
          {unsaved && <li>{t('taxonomy.exportWarning.unsaved')}</li>}
        </ul>
      </FormDialog.Body>
      <FormDialog.Footer
        primary={{ label: t('taxonomy.exportWarning.confirm'), onClick: onConfirm }}
        secondary={{ label: t('taxonomy.grid.cancel'), onClick: () => onOpenChange(false) }}
      />
    </FormDialog>
  );
}
